<template>
  <Modal v-if="value" :value="true" :with-footer="false" @input="closeModal">
    <span slot="header">Ativar acompanhamento de CRLV</span>

    <div class="row">
      <div class="col-md-12">
        <p class="mb-3">O CRLV digital de cada veículo é acompanhado e anexado ao cadastro automaticamente.</p>

        <div v-if="loading" class="mb-3">Conferindo os veículos...</div>

        <template v-else>
          <div class="alert alert-info text-left mb-3">
            <p class="mb-0"><strong>Valor: {{ unitPrice }} por documento entregue</strong></p>
            <p v-if="preview.negotiated" class="small mb-0 mt-1">Valor negociado no seu contrato.</p>
            <p v-else-if="priceTableText" class="small mb-0 mt-1">Tabela por quantidade de placas já ativadas: {{ priceTableText }}.</p>
          </div>

          <p v-if="singleBlockedReason" class="mb-2">
            Este veículo não pode ter o acompanhamento do CRLV ativado agora: <strong>{{ singleBlockedReason }}</strong>.
            <span v-if="singleBlockedKey === 'no_sne'">Habilite o SNE nos serviços do veículo e tente de novo.</span>
          </p>
          <template v-else>
            <p v-if="single" class="mb-1">Este veículo está pronto para o acompanhamento do CRLV.</p>
            <p v-else class="mb-1"><strong>{{ preview.eligible }}</strong> veículo(s) selecionado(s) podem ter o acompanhamento do CRLV ativado.</p>
            <p v-if="!single && skippedText" class="small text-muted mb-2">Ficam de fora da seleção: {{ skippedText }}.</p>
          </template>
          <p class="small text-muted mb-3">
            Os documentos são anexados ao longo do dia. Você acompanha o andamento na coluna CRLV.
          </p>

          <div v-if="!singleBlockedReason" class="term-box mb-3">
            <div class="term-box__header">
              <strong>{{ term ? term.title : 'Termo de Adesão' }}</strong>
              <small v-if="term" class="text-muted">versão {{ term.version }}</small>
            </div>
            <div class="term-box__body">
              <span v-if="termLoading">Carregando os termos...</span>
              <span v-else-if="termError">Não foi possível carregar os termos agora. Feche e tente de novo.</span>
              <template v-else>{{ term && term.text }}</template>
            </div>
          </div>

          <label v-if="!singleBlockedReason" class="accept-row">
            <input type="checkbox" v-model="accepted" :disabled="!term">
            <span v-if="preview.negotiated">Li e aceito o Termo de Adesão acima e o valor negociado de {{ unitPrice }} por documento entregue.</span>
            <span v-else>Li e aceito o Termo de Adesão e a tabela de valores acima. Nesta ativação o valor é de {{ unitPrice }} por documento entregue.</span>
          </label>
        </template>

        <div class="text-right mt-3">
          <button class="btn btn-outline btn-md mr-2" @click="closeModal" :disabled="saving">Cancelar</button>
          <button
            class="btn btn-md btn-primary"
            :disabled="loading || saving || !term || !preview.unit_price_cents || !accepted || !preview.eligible"
            @click="confirm"
          >
            Aceito e ativar
          </button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
  import Modal from '@/components/shares/specialTag/Modal'

  const SKIPPED_LABELS = {
    no_sne: 'sem SNE habilitado',
    already_captured: 'já com CRLV do exercício atual',
    pending: 'com acompanhamento já ativado',
    disabled: 'inativos'
  }

  export default {
    name: 'ModalActivateCrlv',
    components: { Modal },
    props: {
      value: { default: false, type: Boolean },
      vehicleIds: { default: () => [], type: Array }
    },
    data() {
      return {
        accepted: false,
        loading: false,
        saving: false,
        term: null,
        termLoading: false,
        termError: false,
        preview: { eligible: 0, skipped: {}, unit_price_cents: 0, price_table: [] }
      }
    },
    computed: {
      unitPrice() {
        return this.preview.unit_price_cents ? this.formatCents(this.preview.unit_price_cents) : '-'
      },
      priceTableText() {
        const tiers = this.preview.price_table || []
        return tiers.map((tier, index) => {
          const previous = index ? tiers[index - 1].up_to : null
          let range = `até ${this.formatCount(tier.up_to)} placas`
          if (tier.up_to === null || tier.up_to === undefined)
            range = `acima de ${this.formatCount(previous)} placas`
          else if (previous)
            range = `${this.formatCount(previous + 1)} a ${this.formatCount(tier.up_to)} placas`
          return `${range} ${this.formatCents(tier.unit_price_cents)}`
        }).join(' · ')
      },
      single() {
        return this.vehicleIds.length === 1
      },
      singleBlockedKey() {
        if (!this.single || this.preview.eligible)
          return null
        const entry = Object.entries(this.preview.skipped || {}).find(([, count]) => count > 0)
        return entry ? entry[0] : null
      },
      singleBlockedReason() {
        return this.singleBlockedKey ? (SKIPPED_LABELS[this.singleBlockedKey] || this.singleBlockedKey) : null
      },
      skippedText() {
        return Object.entries(this.preview.skipped || {})
          .filter(([, count]) => count > 0)
          .map(([key, count]) => `${count} ${SKIPPED_LABELS[key] || key}`)
          .join(', ')
      },
      params() {
        return this.vehicleIds.length ? { vehicle_ids: this.vehicleIds } : {}
      }
    },
    watch: {
      value(open) {
        if (open)
          this.loadPreview()
      }
    },
    created() {
      if (this.value)
        this.loadPreview()
    },
    methods: {
      formatCents(cents) {
        return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      },
      formatCount(value) {
        return Number(value || 0).toLocaleString('pt-BR')
      },
      closeModal() {
        if (!this.saving)
          this.$emit('input', false)
      },
      loadTerm() {
        if (this.term)
          return
        this.termLoading = true
        this.termError = false
        this.axios.get('vehicle_crlv_captures/term')
          .then(response => this.term = response.data || null)
          .catch(() => this.termError = true)
          .finally(() => this.termLoading = false)
      },
      loadPreview() {
        this.accepted = false
        this.loading = true
        this.loadTerm()
        const request = this.vehicleIds.length > 200
          ? this.axios.post('vehicle_crlv_captures/preview', this.params)
          : this.axios.get('vehicle_crlv_captures/preview', { params: this.params })
        request
          .then(response => this.preview = response.data || this.preview)
          .catch(e => this.$notify.addError(e))
          .finally(() => this.loading = false)
      },
      confirm() {
        this.saving = true
        this.axios.post('vehicle_crlv_captures', { ...this.params, accepted: true, term_version: this.term && this.term.version, unit_price_cents: this.preview.unit_price_cents })
          .then(response => {
            this.$notify.add((response.meta && response.meta.notice) || 'Acompanhamento de CRLV ativado')
            this.$emit('activated')
            this.$emit('input', false)
          })
          .catch(e => {
            this.$notify.addError(e)
            this.loadPreview()
          })
          .finally(() => this.saving = false)
      }
    }
  }
</script>

<style scoped>
  .term-box {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    text-align: left;
  }

  .term-box__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 10px;
    border-bottom: 1px solid #dee2e6;
    font-size: 12px;
  }

  .term-box__body {
    max-height: 180px;
    overflow-y: auto;
    padding: 8px 10px;
    font-size: 11px;
    line-height: 1.5;
    color: #555;
    white-space: pre-wrap;
  }

  .accept-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    cursor: pointer;
    font-weight: normal;
  }

  .accept-row input {
    margin-top: 3px;
  }
</style>
